export const ADMIN_FEATURE_IDS = [
    "dashboard",
    "check_in",
    "attendance",
    "events",
    "rsvps",
    "content",
    "inbox",
    "settings",
    "sms",
    "users",
];
export const ADMIN_FEATURES = [
    { id: "dashboard", label: "Dashboard", description: "Switchboard: featured overnight, visibility, hubs", group: "Core" },
    { id: "check_in", label: "Check-in desk", description: "Door desk and tablet self check-in", group: "Events" },
    { id: "attendance", label: "Attendance", description: "Checked-in report, CSV, and email", group: "Events" },
    { id: "events", label: "Event calendar", description: "Create and edit nights, gallery, and stream", group: "Events" },
    { id: "rsvps", label: "RSVPs", description: "Reservations inbox", group: "Events" },
    { id: "content", label: "Content", description: "Content hub: blog, pages, copy, YouTube, home video", group: "Site" },
    { id: "inbox", label: "Inbox", description: "People hub: testimonies, participation, partners, messages, subscribers, comments, address book (view)", group: "Site" },
    { id: "settings", label: "Site settings", description: "General, contacts, social, footer, email, and YouTube API", group: "Admin" },
    { id: "sms", label: "SMS & contacts", description: "Address book (write), SMS broadcasts, and Africa's Talking credentials", group: "Admin" },
    { id: "users", label: "Users & permissions", description: "Create accounts and assign feature access", group: "Admin" },
];
export function isAdminFeatureId(value) {
    return ADMIN_FEATURE_IDS.includes(value);
}
export function isFullAdmin(user) {
    if (!user || user.role !== "ADMIN")
        return false;
    const perms = user.permissions ?? [];
    return perms.length === 0 || perms.includes("*");
}
export function hasPermission(user, feature) {
    if (!user || user.role !== "ADMIN")
        return false;
    if (isFullAdmin(user))
        return true;
    return (user.permissions ?? []).includes(feature);
}
export function hasAnyPermission(user, features) {
    return features.some((feature) => hasPermission(user, feature));
}
export function sanitizeFeatureIds(values) {
    const seen = new Set();
    const out = [];
    for (const value of values ?? []) {
        if (!isAdminFeatureId(value) || seen.has(value))
            continue;
        seen.add(value);
        out.push(value);
    }
    return out;
}
/** Empty list means full access (legacy admins). */
export function permissionsForSave(fullAccess, selected) {
    if (fullAccess)
        return [];
    return sanitizeFeatureIds(selected);
}
export function firstAdminPath(user) {
    const order = [
        ["dashboard", "/admin"],
        ["check_in", "/admin/tonight"],
        ["attendance", "/admin/tonight"],
        ["events", "/admin/tonight"],
        ["rsvps", "/admin/tonight"],
        ["content", "/admin/content-hub"],
        ["inbox", "/admin/people"],
        ["settings", "/admin/settings/general"],
        ["sms", "/admin/people"],
        ["users", "/admin/settings/users"],
    ];
    for (const [feature, path] of order) {
        if (hasPermission(user, feature))
            return path;
    }
    return "/";
}
export function featuresForAdminPath(pathname) {
    if (pathname === "/admin" || pathname === "/admin/")
        return ["dashboard"];
    if (pathname.startsWith("/admin/tonight")) {
        return ["check_in", "attendance", "events", "rsvps"];
    }
    if (pathname.startsWith("/admin/content-hub") ||
        pathname.startsWith("/admin/studio")) {
        return ["content", "events"];
    }
    if (pathname.startsWith("/admin/people/address-book") ||
        pathname.startsWith("/admin/address-book")) {
        return ["inbox", "sms"];
    }
    if (pathname.startsWith("/admin/people"))
        return ["inbox", "sms"];
    if (pathname.startsWith("/admin/check-in"))
        return ["check_in"];
    if (pathname.startsWith("/admin/attendance"))
        return ["attendance"];
    if (pathname.startsWith("/admin/events") || pathname.startsWith("/admin/gallery") || pathname.startsWith("/admin/live") || pathname.startsWith("/admin/replays")) {
        return ["events"];
    }
    if (pathname.startsWith("/admin/rsvps"))
        return ["rsvps"];
    if (pathname.startsWith("/admin/blog") ||
        pathname.startsWith("/admin/content") ||
        pathname.startsWith("/admin/copy") ||
        pathname.startsWith("/admin/youtube")) {
        return ["content"];
    }
    if (pathname.startsWith("/admin/testimonials") ||
        pathname.startsWith("/admin/participation") ||
        pathname.startsWith("/admin/partners") ||
        pathname.startsWith("/admin/contact") ||
        pathname.startsWith("/admin/subscribers") ||
        pathname.startsWith("/admin/comments")) {
        return ["inbox"];
    }
    if (pathname.startsWith("/admin/communication"))
        return ["sms"];
    if (pathname.startsWith("/admin/settings/sms"))
        return ["sms"];
    if (pathname.startsWith("/admin/settings/address-book"))
        return ["inbox", "sms"];
    if (pathname.startsWith("/admin/settings/users"))
        return ["users"];
    if (pathname.startsWith("/admin/settings") || pathname.startsWith("/admin/site")) {
        return ["settings"];
    }
    if (pathname.startsWith("/admin"))
        return ["dashboard"];
    return null;
}
export function firstSettingsPath(user) {
    if (hasPermission(user, "settings"))
        return "/admin/settings/general";
    if (hasPermission(user, "sms"))
        return "/admin/settings/sms";
    if (hasPermission(user, "inbox"))
        return "/admin/settings/address-book";
    if (hasPermission(user, "users"))
        return "/admin/settings/users";
    return firstAdminPath(user);
}
export function describePermissions(user) {
    if (user.role !== "ADMIN")
        return "User";
    if (isFullAdmin(user))
        return "Admin · full access";
    const labels = sanitizeFeatureIds(user.permissions).map((id) => ADMIN_FEATURES.find((f) => f.id === id)?.label ?? id);
    return labels.length ? `Admin · ${labels.join(", ")}` : "Admin";
}
//# sourceMappingURL=permissions.js.map